import { SerializedStyles } from '@emotion/react'
import {
  ButtonHTMLAttributes,
  ChangeEventHandler,
  InputHTMLAttributes,
  ReactNode,
} from 'react'

export enum ChipStyleKeys {
  root = 'root',
  label = 'label',
  input = 'input',
  chip = 'chip',
  iconBox = 'iconBox',
  checked = 'checked',
  disabled = 'disabled',
}

export const acceptedRoots = ['button', 'checkbox', 'radio'] as const
export type AcceptedRoot = typeof acceptedRoots[number]

export const variants = ['main', 'outline'] as const
export type Variant = typeof variants[number]

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  value?: undefined
  onChange?: undefined
  checked?: undefined
}

export type CheckboxProps = InputHTMLAttributes<HTMLInputElement> & {
  value?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  checked?: boolean
}

export type RadioProps = InputHTMLAttributes<HTMLInputElement> & {
  value?: string
  onChange?: ChangeEventHandler<HTMLInputElement>
  checked?: boolean
}

export interface ChipProps {
  styles?: Partial<Record<ChipStyleKeys, SerializedStyles>>
  disabled: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
  renderAs?: AcceptedRoot
  variant?: Variant
}
export interface useStateStylesReturn {
  activeStyles: SerializedStyles[]
  disabledStyles: SerializedStyles[]
}
export interface useStateStylesProps extends Partial<ChipProps> {
  overrides?: ChipProps['styles']
  checked: boolean
}
export interface IUseStateStyles {
  (props: useStateStylesProps): useStateStylesReturn
}

export const isButtonProps = (
  props: (ButtonProps | RadioProps | CheckboxProps) & ChipProps,
): props is ButtonProps & ChipProps => props.renderAs === 'button'

export const isCheckboxProps = (
  props: (ButtonProps | RadioProps | CheckboxProps) & ChipProps,
): props is CheckboxProps & ChipProps => props.renderAs === 'checkbox'

export const isRadioProps = (
  props: (ButtonProps | RadioProps | CheckboxProps) & ChipProps,
): props is RadioProps & ChipProps => props.renderAs === 'radio'

export const isAcceptedRoot = (value: AcceptedRoot): value is AcceptedRoot =>
  acceptedRoots.includes(value)

export const isVariant = (value: Variant): value is Variant =>
  variants.includes(value)
